// timer.service.ts
import { Injectable, OnDestroy, inject } from '@angular/core';
import {
  addSeconds,
  differenceInMinutes,
  differenceInSeconds,
  isAfter,
  subSeconds,
} from 'date-fns';
import { BehaviorSubject, Subscription, timer } from 'rxjs';
import { finalize, takeWhile } from 'rxjs/operators';
import { HistoryRepository } from 'src/app/analytics/state/history.repository';
import { SettingsRepository } from 'src/app/settings/state/settings.repository';
import { boundaryFor, dayKeyFor } from 'src/app/utilities/day-key.utils';
import { TimestampRepository } from './timestamps.repository';
import { TimerAction } from './timer.interface';

import { FirebaseSyncService } from 'src/app/services/firebase-sync/firebase-sync.service';
import { NotificationService } from 'src/app/shared/services/notification/notification.service';
import { secondsToHHMMSS } from 'src/app/utilities/data-formatting';
import { TimestampService } from './timestamps.service';

export enum Mode {
  Focus = 'focus',
  Rest = 'rest',
  Stop = 'stopped',
  Ended = 'ended',
}

const IDLE_PAUSE_MINUTES = 10;

/**
 * TODOS:
 * - Use timestamp instead of timer -> Fixes issue when tab is in background;
 * - Save settings in LocalStorage
 * - Expose Reminder Settings
 * - Refactor for cleaness and testing
 */

@Injectable({
  providedIn: 'root',
})
export class TimerService implements OnDestroy {
  private settings = inject(SettingsRepository);
  private timestamps = inject(TimestampRepository);
  private timestampService = inject(TimestampService);
  private history = inject(HistoryRepository);
  private sync = inject(FirebaseSyncService);
  private notification = inject(NotificationService);

  formattedTime$ = new BehaviorSubject<string>('00:00');

  private remainingRestSeconds = 0;
  private totalRestSeconds = 0;

  private timerSubscription: Subscription | null = null;
  private reminderSubscription: Subscription | null = null;

  // Every subscription/listener started in the constructor (as opposed to the ones above, which
  // are transient and already self-managed by clearTimerSubscription/clearReminderNotifications)
  // must be released in ngOnDestroy — otherwise this root service's 1s reconcile tick and DOM
  // listener outlive whatever created it, which is what caused leaked instances across Karma runs.
  private reconcileSubscription: Subscription | null = null;
  private lastEntrySubscription: Subscription | null = null;
  private focusReminderIntervalSubscription: Subscription | null = null;
  private websiteBlockerSubscription: Subscription | null = null;
  private formattedTimeInternalSubscription: Subscription | null = null;
  private onVisibilityChange = () => {
    if (typeof document !== 'undefined' && !document.hidden) {
      this.reconcile();
    }
  };

  private restShouldEndAt: Date | null = null;

  private restAudio: HTMLAudioElement;

  private actionRunning: TimerAction = TimerAction.Pause;

  // False until the first reconcile() of this app instance. The idle-Pause rule answers "was the
  // app closed while a timer ran?", and that is only knowable at startup — after that, a gap in the
  // heartbeat means the browser froze our backgrounded tab, not that the user walked away.
  private hasReconciledOnce = false;

  constructor() {
    this.focusReminderIntervalSubscription = this.settings.focusReminderInterval$.subscribe(() => {
      // updates focus reminder timing when the interval changes
      this.setFocusReminders();
    });

    this.handleExtension();

    this.reconcile();

    this.reconcileSubscription = timer(1000, 1000).subscribe(() => this.reconcile());

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    }

    this.restAudio = new Audio('assets/audios/rest_end.mp3');

    this.lastEntrySubscription = this.timestamps.lastEntry$.subscribe((entry) => {
      if (!entry) {
        return;
      }
      // While the Adjust History sheet is open, the user is mutating past entries.
      // Auto-reacting to lastEntry changes here is what causes the Pause/Rest lockout
      // loop (deleting a Pause makes Rest the new last entry, which restarts the Rest
      // timer and re-fires its finalize-Stop). Suspend until the sheet closes, then
      // rederive once from the final saved or unchanged timeline.
      if (this.timestamps.isEditingHistory()) {
        return;
      }
      // Prevents double actions
      if (this.actionRunning !== entry.action) {
        this.actionRunning = entry.action;
      } else {
        return;
      }

      this.dispatchAction(entry.action);
    });
  }

  ngOnDestroy(): void {
    this.clearTimerSubscription();
    this.clearReminderNotifications();

    this.reconcileSubscription?.unsubscribe();
    this.lastEntrySubscription?.unsubscribe();
    this.focusReminderIntervalSubscription?.unsubscribe();
    this.websiteBlockerSubscription?.unsubscribe();
    this.formattedTimeInternalSubscription?.unsubscribe();

    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }
  }

  /**
   * Re-syncs the running timer with the current last entry without going through the
   * lastEntry$ subscription. Called after the Adjust History sheet applies its changes.
   */
  rederiveTimerState() {
    const entities = this.timestamps.getEntries();
    const lastEntry = entities[entities.length - 1];

    if (!lastEntry) {
      this.handleStopTimer();
      this.actionRunning = TimerAction.Stop;
      this.emitFormattedTime(0);
      return;
    }

    this.actionRunning = lastEntry.action;
    this.dispatchAction(lastEntry.action);
  }

  /**
   * Suspends any running countdown/reminders so an in-flight Rest's finalize block
   * can't add a stray Stop while the user edits past entries.
   */
  suspendForEditing() {
    this.clearTimerSubscription();
    this.clearReminderNotifications();
  }

  private dispatchAction(action: TimerAction) {
    switch (action) {
      case TimerAction.Focus:
        this.handleStartFocus();
        break;
      case TimerAction.Rest:
        this.handleStartRest();
        break;
      case TimerAction.Pause:
        this.handlePauseTimer();
        break;
      case TimerAction.Resume:
        this.handleResume();
        break;
      case TimerAction.Stop:
        this.handleStopTimer();
        break;
    }
  }

  startFocus(resuming = false) {
    if (!resuming) {
      this.stopTimer();
      this.timestampService.addEntryAndSync(TimerAction.Focus);
    }
  }

  startRest(resuming = false) {
    if (!resuming) {
      this.stopTimer();
      this.timestampService.addEntryAndSync(TimerAction.Rest);
    }
  }

  togglePause() {
    const paused = this.timestamps.isPaused();
    if (!paused) {
      this.pauseTimer();
    } else {
      this.resumeTimer();
    }
    this.sync.scheduleSaveToday();
  }

  pauseTimer() {
    this.timestampService.addEntryAndSync(TimerAction.Pause);
  }

  resumeTimer() {
    this.timestampService.addEntryAndSync(TimerAction.Resume);
  }

  stopTimer() {
    // Reset the total rest seconds with the remaining from the last round;
    // This allows the user to stop the rest prior to ending

    if (this.reminderSubscription || this.timerSubscription) {
      this.timestampService.addEntryAndSync(TimerAction.Stop);
    }
  }

  getRemainingRest(): number {
    // Focus time is already rendered from persisted timestamps rather than from the
    // number of interval callbacks received. Derive the balance on that same display
    // tick so a throttled/background tab catches up from wall-clock time immediately
    // without introducing another polling loop.
    if (this.timestamps.getCurrentMode() === TimerAction.Focus) {
      this.updateTotalRest();
      this.remainingRestSeconds = this.totalRestSeconds;
    }

    return Math.max(0, this.remainingRestSeconds);
  }

  private emitFormattedTime(seconds: number) {
    if (seconds < 0) {
      seconds = 0;
    }

    const formatted = secondsToHHMMSS(seconds);

    this.formattedTime$.next(formatted);
  }

  private setFocusReminders() {
    this.clearReminderNotifications();
    const mode = this.timestamps.getCurrentMode();
    if (mode !== TimerAction.Focus || this.timestamps.isPaused()) {
      return;
    }

    // create an array of notifications to be sent, every 5 minutes
    // use a timestamp to determine the time for each notification
    // fire the notification when the timestamp has passed by and remove it from the array
    const notifications = new Array(360).fill(0).map((_, index) => {
      return {
        timestamp:
          new Date().getTime() + 1000 * 60 * this.settings.getFocusReminderInterval() * (index + 1),
        message: this.getRandomReminder(),
      };
    });

    this.reminderSubscription = timer(1000, 1000).subscribe(() => {
      const nextNotification = notifications[0];

      if (nextNotification && isAfter(new Date(), nextNotification.timestamp)) {
        // removes the next one from the array
        notifications.shift();

        if (this.settings.isEndTimerReminderEnabled()) {
          this.notification.notify(nextNotification.message, 5000);
        }
      }
    });
  }

  private getRandomReminder() {
    const reminders = this.settings.getRemindersList();
    const randomIndex = Math.floor(Math.random() * reminders.length);
    return reminders[randomIndex];
  }

  private clearReminderNotifications() {
    if (this.reminderSubscription) {
      this.reminderSubscription.unsubscribe();
      this.reminderSubscription = null;
    }
  }

  private clearTimerSubscription() {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
      this.timerSubscription = null;
    }
  }

  private handleResume() {
    const mode = this.timestamps.getCurrentMode();
    switch (mode) {
      case TimerAction.Focus:
        this.handleStartFocus();
        break;
      case TimerAction.Rest:
        this.totalRestSeconds = this.remainingRestSeconds;
        this.handleStartRest();
        break;
    }
  }

  private handleStartFocus() {
    // we need to handle stop in case there are several windows opened
    this.handleStopTimer();

    this.restShouldEndAt = null;
    this.setFocusReminders();

    // start timer whenever the clock is at 0ms
    const currentMs = new Date().getMilliseconds();
    const msUntilNextFullSecond = 1000 - currentMs;

    this.timerSubscription = timer(msUntilNextFullSecond, 1000).subscribe(() => {
      this.emitFormattedTime(this.timestamps.getCurrentFocusedSeconds());
    });
  }

  private handleStartRest() {
    // we need to handle stop in case there are several windows opened
    this.handleStopTimer();

    this.remainingRestSeconds = this.totalRestSeconds;

    this.restShouldEndAt = addSeconds(new Date(), this.remainingRestSeconds);

    const currentMs = new Date().getMilliseconds();
    const msUntilNextFullSecond = 1000 - currentMs;

    this.timerSubscription = timer(msUntilNextFullSecond, 1000)
      .pipe(
        takeWhile(() => this.remainingRestSeconds > 0),
        finalize(() => {
          this.clearTimerSubscription();

          this.emitFormattedTime(this.remainingRestSeconds);

          if (this.remainingRestSeconds <= 0) {
            const restEndTime = addSeconds(
              this.timestamps.getLastRestStartTimestamp()!,
              this.totalRestSeconds,
            );

            const timestamp = restEndTime ?? new Date();
            this.timestampService.addEntryAndSync(TimerAction.Stop, timestamp.toString());

            if (this.settings.isEndTimerReminderEnabled()) {
              this.notification.notify('Rest Finished');
              if (this.settings.isSoundEnabled() && this.restAudio.readyState === 4) {
                this.restAudio.play();
              }
            }
          }
        }),
      )
      .subscribe(() => {
        // calculate the remaining rest seconds based on the start Time and totalRest time
        this.remainingRestSeconds =
          this.totalRestSeconds -
          differenceInSeconds(new Date(), this.timestamps.getLastRestStartTimestamp()!);

        this.emitFormattedTime(this.remainingRestSeconds);
      });
  }

  private handlePauseTimer() {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
      this.timerSubscription = null;
    }

    this.clearReminderNotifications();

    this.emitCurrentTime();
  }

  private handleStopTimer() {
    this.clearReminderNotifications();

    this.updateTotalRest();

    this.clearTimerSubscription();
  }

  /**
   * Single owner of the alive heartbeat, idle detection and day rollover.
   *
   * These MUST share one handler. The heartbeat resumes the instant the machine wakes; if it ran as
   * a separate subscription it could overwrite lastAliveTimestamp before the gap was read, erasing
   * both the retroactive Pause and the rollover. Reading before writing also makes the handler safe
   * to call from several triggers at once — the second call simply sees a gap of ~0.
   *
   * Public so lifecycle hooks (constructor bootstrap, the visibilitychange listener, and the tests
   * in reconcile.spec.ts) can all call the same idempotent entry point.
   */
  reconcile(): void {
    const previous = this.settings.getLastAliveTimestamp();
    const now = Date.now();
    this.settings.setLastAliveTimestamp(now);

    // A missing or corrupt persisted heartbeat must not crash the timer: dayKeyFor() throws
    // (date-fns format() rejects an Invalid Date) if handed an unparseable instant. Degrade safely
    // by skipping the gap/rollover logic rather than letting a bad localStorage value take the
    // app down — this is the same failure mode reported from leaked, never-destroyed instances.
    if (!previous || Number.isNaN(new Date(previous).getTime())) {
      console.error('reconcile: unparseable lastAliveTimestamp, skipping rollover', previous);
      return;
    }

    const dayStartHour = this.settings.getDayStartHour();
    const previousKey = dayKeyFor(previous, dayStartHour);
    const currentKey = dayKeyFor(now, dayStartHour);

    // Was the app itself running continuously up to now? A large gap measured on the FIRST
    // reconcile means the app was closed (or the device was off) for that long. The same gap on a
    // later reconcile means only that our own 1-second heartbeat stopped ticking — which browsers
    // do routinely, throttling and then freezing a backgrounded tab while the user works in another
    // app. Those are different facts and must not share a branch.
    const wasBootstrap = !this.hasReconciledOnce;
    this.hasReconciledOnce = true;
    const idleGap = differenceInMinutes(now, previous) >= IDLE_PAUSE_MINUTES;

    if (wasBootstrap && idleGap && this.timestamps.isAccruingOn(previousKey)) {
      // Pauses the last timer at the moment the app was last known alive, so time while it was
      // closed is not credited. Startup-only by design: this rule answers "was the app closed?",
      // which is only knowable when the app starts.
      this.timestampService.addEntryAndSync(TimerAction.Pause, new Date(previous).toString());
    }

    if (previousKey !== currentKey) {
      // A boundary reached while the heartbeat was alive is a real crossing the user worked
      // through; one discovered after a gap means the app was not running when it happened.
      this.closeDay(previousKey, dayStartHour, previous, !idleGap);
    }

    // The pointer is a display cache, never a write authority (every write derives its own day from
    // the entry's own timestamp) — but nothing else keeps it pointed at "today" now that
    // constructor-time wall-clock syncing is gone (that re-entry point, HistoryRepository's former
    // syncActiveDay, was removed as dead code — this is the only place the pointer gets corrected).
    // Correct it unconditionally, every reconcile, so a brand-new install's first session and every
    // ordinary same-day reopen show live "today" data immediately, not only after an actual
    // boundary crossing.
    this.history.setActiveDay(currentKey);

    if (this.settings.getPreserveTimer() && !this.timerSubscription) {
      // Runs if the app opens but there was an active timer running. Gated on !timerSubscription
      // because once a Focus/Rest countdown loop is running it already re-derives and emits every
      // second on its own — calling emitCurrentTime() again here would fight it, and in the
      // non-accumulate-rest setting would briefly stomp the live Rest countdown to 0 every tick
      // (updateTotalRest()'s non-accumulate branch reads getCurrentFocusedSeconds(), which is
      // defined to be 0 whenever the current mode isn't Focus).
      this.emitCurrentTime();
    }
  }

  /**
   * Terminates a logical day that the clock has left behind. A Rest in flight at the edge is ended
   * rather than resumed: its allowance was earned from this day's focus, so carrying it over would
   * give the new day un-earned rest and lock validateHistoryEdit.
   *
   * Derives the day to close from the previous heartbeat's own timestamp — never from the active
   * pointer — so an absence spanning many boundaries closes only the one day that actually has
   * entries; intermediate days are never materialised.
   */
  private closeDay(
    dayKey: string,
    dayStartHour: number,
    lastAlive: number,
    aliveAcrossBoundary: boolean,
  ): void {
    const entries = this.history.getDay(dayKey)?.timestamps ?? [];
    if (entries.length === 0) {
      return;
    }

    const wasAccruing = this.timestamps.isAccruingOn(dayKey);
    const modeAtEdge = this.timestamps.getLastModeWithoutPauses(entries);
    const edge = boundaryFor(dayKey, dayStartHour);
    const edgeStop = subSeconds(edge, 1);

    // Where the day actually ended. Alive across the boundary means the user really did work up to
    // it, so the day closes at the edge. Discovered after a gap means the app was not running when
    // the boundary passed, and closing at the edge would credit every minute from the last
    // heartbeat to 04:00 as focus. Never place the Stop before the entry it terminates.
    const lastEntryAt = new Date(entries[entries.length - 1].timestamp).getTime();
    const stopAt = aliveAcrossBoundary
      ? edgeStop
      : new Date(Math.max(lastEntryAt, Math.min(edgeStop.getTime(), lastAlive)));

    this.timestampService.addEntryAndSync(TimerAction.Stop, stopAt.toString());

    // Only a session the user was demonstrably still running continues onto the new day. Re-opening
    // Focus after a gap would invent a session that started while the app was closed.
    if (aliveAcrossBoundary && wasAccruing && modeAtEdge === TimerAction.Focus) {
      this.timestampService.addEntryAndSync(TimerAction.Focus, edge.toString());
    }
  }

  private updateTotalRest() {
    if (!this.settings.getAccumulateRest()) {
      this.totalRestSeconds =
        this.timestamps.getCurrentFocusedSeconds() / this.timestamps.getMultiplier();
    } else {
      this.totalRestSeconds =
        this.timestamps.getTodaysTime(TimerAction.Focus) / this.timestamps.getMultiplier() -
        this.timestamps.getTodaysTime(TimerAction.Rest);
    }
  }

  private emitCurrentTime() {
    const mode = this.timestamps.getCurrentMode();

    if (mode === TimerAction.Focus || mode === TimerAction.Rest) {
      if (mode === TimerAction.Focus) {
        this.emitFormattedTime(this.timestamps.getCurrentFocusedSeconds());
      } else {
        this.updateTotalRest();
        this.remainingRestSeconds = this.totalRestSeconds;
        this.emitFormattedTime(this.remainingRestSeconds);
      }
    }
  }

  private handleExtension() {
    /*  window.addEventListener('message', (event) => {
      if (event.data.requestFocus && this.timestamps.getCurrentMode() === TimerAction.Stop) {
        this.startFocus();
        event.source?.postMessage({ focusStarted: true });
      }
    }); */

    this.websiteBlockerSubscription = this.settings.websiteBlockerEnabled$.subscribe(
      (isEnabled) => {
        if (!isEnabled && window && window.postMessage) {
          window.postMessage({ mode: 'rest' });
          return;
        }
      },
    );

    this.formattedTimeInternalSubscription = this.formattedTime$.subscribe((ellapsedTime) => {
      if (!this.settings.isWebsiteBlockerEnabled()) {
        window.postMessage({ enabled: false });
        return;
      }

      const mode = this.timestamps.getCurrentMode();
      try {
        if (mode === TimerAction.Focus && window && window.postMessage) {
          const whitelist = this.settings.getWhitelistItems() || '';
          window.postMessage({
            enabled: true,
            mode: 'focus',
            ellapsedTime,
            whitelist,
            hyperBlockEnabled: this.settings.getHyperBlockEnabled(),
          });
        } else {
          window.postMessage({ enabled: true, mode: 'rest' });
        }
      } catch (e) {}
    });
  }
}
